import {
  DeesElement,
  type TemplateResult,
  property,
  customElement,
  html,
  css,
  cssManager,
} from '@design.estate/dees-element';

import {
  type ITheme,
  type IThemeSpacing,
  type IThemeRadius,
  type IThemeShadows,
  type IThemeTransitions,
  type IThemeEasings,
  type IThemeTypography,
  type IThemeTypographyStyle,
  type IThemeMaterials,
  type IThemeMaterial,
  type IThemeControlHeights,
  themeDefaults,
  themeDefaultStyles,
} from '../../00theme.js';

import { demoFunc } from './dees-theme.demo.js';

/**
 * Convert a typography style key (e.g. `largeTitle`, `title1`) to its CSS
 * custom-property segment (`large-title`, `title1`). Must match 00theme.ts.
 */
const typographyKeyToCssSegment = (name: string): string =>
  name.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase();

/**
 * A theme provider component that wraps children and provides CSS custom properties.
 * Can be used at the app root or around specific sections to customize theming.
 *
 * Usage:
 * ```html
 * <dees-theme>
 *   <my-app></my-app>
 * </dees-theme>
 * ```
 *
 * With custom overrides:
 * ```html
 * <dees-theme .customSpacing=${{ lg: '20px' }}>
 *   <my-section></my-section>
 * </dees-theme>
 * ```
 */
@customElement('dees-theme')
export class DeesTheme extends DeesElement {
  public static demo = demoFunc;
  public static demoGroups = ['Utility'];

  // ============================================
  // Properties for theme overrides
  // ============================================

  @property({ type: Object })
  accessor customSpacing: Partial<IThemeSpacing> | null = null;

  @property({ type: Object })
  accessor customRadius: Partial<IThemeRadius> | null = null;

  @property({ type: Object })
  accessor customShadows: Partial<IThemeShadows> | null = null;

  @property({ type: Object })
  accessor customTransitions: Partial<IThemeTransitions> | null = null;

  @property({ type: Object })
  accessor customEasings: Partial<IThemeEasings> | null = null;

  @property({ type: Object })
  accessor customTypography: Partial<Record<keyof IThemeTypography, Partial<IThemeTypographyStyle>>> | null = null;

  @property({ type: Object })
  accessor customMaterials: Partial<Record<keyof IThemeMaterials, Partial<IThemeMaterial>>> | null = null;

  @property({ type: Object })
  accessor customControlHeights: Partial<IThemeControlHeights> | null = null;

  // ============================================
  // Styles
  // ============================================

  public static styles = [
    themeDefaultStyles,
    cssManager.defaultStyles,
    css`
      :host {
        display: contents;
      }
    `,
  ];

  // ============================================
  // Render
  // ============================================

  public render(): TemplateResult {
    return html`
      <style>
        ${this.generateCustomStyles()}
      </style>
      <slot></slot>
    `;
  }

  // ============================================
  // Private Methods
  // ============================================

  private generateCustomStyles(): string {
    const styles: string[] = [':host {'];

    if (this.customSpacing) {
      for (const [key, value] of Object.entries(this.customSpacing)) {
        if (value) styles.push(`  --dees-spacing-${key}: ${value};`);
      }
    }

    if (this.customRadius) {
      for (const [key, value] of Object.entries(this.customRadius)) {
        if (value) styles.push(`  --dees-radius-${key}: ${value};`);
      }
    }

    if (this.customShadows) {
      for (const [key, value] of Object.entries(this.customShadows)) {
        if (value) styles.push(`  --dees-shadow-${key}: ${value};`);
      }
    }

    if (this.customTransitions) {
      for (const [key, value] of Object.entries(this.customTransitions)) {
        if (value) styles.push(`  --dees-transition-${key}: ${value};`);
      }
    }

    if (this.customEasings) {
      for (const [key, value] of Object.entries(this.customEasings)) {
        if (value) styles.push(`  --dees-ease-${key}: ${value};`);
      }
    }

    if (this.customControlHeights) {
      for (const [key, value] of Object.entries(this.customControlHeights)) {
        if (value) styles.push(`  --dees-control-height-${key}: ${value};`);
      }
    }

    if (this.customTypography) {
      for (const [name, style] of Object.entries(this.customTypography)) {
        if (!style) continue;
        const seg = typographyKeyToCssSegment(name);
        if (style.size) styles.push(`  --dees-font-${seg}-size: ${style.size};`);
        if (style.weight) styles.push(`  --dees-font-${seg}-weight: ${style.weight};`);
        if (style.lineHeight) styles.push(`  --dees-font-${seg}-line: ${style.lineHeight};`);
        if (style.letterSpacing) styles.push(`  --dees-font-${seg}-tracking: ${style.letterSpacing};`);
      }
    }

    if (this.customMaterials) {
      for (const [name, material] of Object.entries(this.customMaterials)) {
        if (!material) continue;
        if (material.bg) styles.push(`  --dees-material-${name}-bg: ${material.bg};`);
        if (material.filter) styles.push(`  --dees-material-${name}-filter: ${material.filter};`);
        if (material.bgOpaque) styles.push(`  --dees-material-${name}-bg-opaque: ${material.bgOpaque};`);
      }
    }

    styles.push('}');
    return styles.join('\n');
  }

  // ============================================
  // Public API Methods
  // ============================================

  /**
   * Set a spacing value dynamically
   */
  public setSpacing(key: keyof IThemeSpacing, value: string): void {
    this.customSpacing = { ...this.customSpacing, [key]: value };
  }

  /**
   * Set a radius value dynamically
   */
  public setRadius(key: keyof IThemeRadius, value: string): void {
    this.customRadius = { ...this.customRadius, [key]: value };
  }

  /**
   * Set a shadow value dynamically
   */
  public setShadow(key: keyof IThemeShadows, value: string): void {
    this.customShadows = { ...this.customShadows, [key]: value };
  }

  /**
   * Set a transition value dynamically
   */
  public setTransition(key: keyof IThemeTransitions, value: string): void {
    this.customTransitions = { ...this.customTransitions, [key]: value };
  }

  /**
   * Set an easing value dynamically
   */
  public setEasing(key: keyof IThemeEasings, value: string): void {
    this.customEasings = { ...this.customEasings, [key]: value };
  }

  /**
   * Set a typography style dynamically (full or partial style)
   */
  public setTypography(key: keyof IThemeTypography, value: Partial<IThemeTypographyStyle>): void {
    this.customTypography = { ...this.customTypography, [key]: value };
  }

  /**
   * Set a material (vibrancy surface) dynamically (full or partial material)
   */
  public setMaterial(key: keyof IThemeMaterials, value: Partial<IThemeMaterial>): void {
    this.customMaterials = { ...this.customMaterials, [key]: value };
  }

  /**
   * Set a control height value dynamically
   */
  public setControlHeight(key: keyof IThemeControlHeights, value: string): void {
    this.customControlHeights = { ...this.customControlHeights, [key]: value };
  }

  /**
   * Get the current theme configuration (defaults + overrides).
   * Note: colors and materials are theme-split (light/dark) and reported as
   * defaults here; runtime material overrides still apply to rendering.
   */
  public getTheme(): ITheme {
    const typography: IThemeTypography = { ...themeDefaults.typography };
    if (this.customTypography) {
      for (const [name, style] of Object.entries(this.customTypography)) {
        if (!style) continue;
        const key = name as keyof IThemeTypography;
        typography[key] = { ...themeDefaults.typography[key], ...style };
      }
    }

    return {
      colors: themeDefaults.colors,
      spacing: { ...themeDefaults.spacing, ...this.customSpacing },
      radius: { ...themeDefaults.radius, ...this.customRadius },
      shadows: { ...themeDefaults.shadows, ...this.customShadows },
      transitions: { ...themeDefaults.transitions, ...this.customTransitions },
      easings: { ...themeDefaults.easings, ...this.customEasings },
      typography,
      materials: themeDefaults.materials,
      controlHeights: { ...themeDefaults.controlHeights, ...this.customControlHeights },
      terminal: themeDefaults.terminal,
    };
  }

  /**
   * Reset all custom overrides to defaults
   */
  public resetToDefaults(): void {
    this.customSpacing = null;
    this.customRadius = null;
    this.customShadows = null;
    this.customTransitions = null;
    this.customEasings = null;
    this.customTypography = null;
    this.customMaterials = null;
    this.customControlHeights = null;
  }

  /**
   * Apply a complete theme object.
   * Colors and materials are theme-split and not applied via runtime overrides.
   */
  public applyTheme(theme: Partial<ITheme>): void {
    if (theme.spacing) this.customSpacing = theme.spacing;
    if (theme.radius) this.customRadius = theme.radius;
    if (theme.shadows) this.customShadows = theme.shadows;
    if (theme.transitions) this.customTransitions = theme.transitions;
    if (theme.easings) this.customEasings = theme.easings;
    if (theme.typography) this.customTypography = theme.typography;
    if (theme.controlHeights) this.customControlHeights = theme.controlHeights;
  }
}
